// Fixed-width (80-column / Pica 10 cpi) plain-text renderers for the
// EPSON LX-310 and other impact/dot-matrix printers.
//
// Why text mode: a 9-pin printer driven through a browser prints the HTML
// page as a rasterised bitmap, which comes out faint/blurry and often
// vertically squished (the driver fits the tall A4 image onto short
// continuous-form paper). Printing PLAIN TEXT instead lets the printer use
// its own internal draft/NLQ font — crisp, fast, correctly spaced.
//
// Everything below is laid out to fit within 80 columns (8" @ 10 cpi).

import { buildAmortizationSchedule } from "./calc";

export const W = 80;

// Zero-width bold markers. These are inserted AFTER all column/width math so
// they never affect alignment. buildEscpDocument() turns them into the
// printer's emphasized (bold) ON/OFF control codes (ESC E / ESC F); the HTML
// text-print popup turns them into <b>/</b>.
export const BOLD_ON = "\u0001";
export const BOLD_OFF = "\u0002";
const b = (s) => BOLD_ON + String(s) + BOLD_OFF;
// Bold only the leading `label` portion of an already-laid-out string.
const boldPrefix = (line, label) =>
  b(label) + String(line).slice(label.length);

const money = (n) =>
  Number(n || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const rep = (c, n) => c.repeat(Math.max(0, n));
const hr = (c = "-") => rep(c, W);

const center = (s) => {
  s = String(s).slice(0, W);
  return rep(" ", Math.floor((W - s.length) / 2)) + b(s);
};

// Wrap text to `width` columns (greedy word wrap; breaks over-long words).
function wrap(text, width = W) {
  const out = [];
  for (const rawLine of String(text).split("\n")) {
    const words = rawLine.split(/\s+/).filter(Boolean);
    let cur = "";
    if (words.length === 0) out.push("");
    for (const wd of words) {
      const next = cur ? cur + " " + wd : wd;
      if (next.length > width) {
        if (cur) out.push(cur);
        if (wd.length > width) {
          let rest = wd;
          while (rest.length > width) {
            out.push(rest.slice(0, width));
            rest = rest.slice(width);
          }
          cur = rest;
        } else {
          cur = wd;
        }
      } else {
        cur = next;
      }
    }
    if (cur) out.push(cur);
  }
  return out;
}

// "Label ................ value" — label (bold) left, value right-aligned to col 80.
function rightVal(label, value, width = W) {
  const v = String(value);
  const gap = width - label.length - v.length;
  if (gap < 1) return b(label) + " " + v;
  return b(label) + rep(" ", gap) + v;
}

// Label in a fixed-width gutter (bold), value wrapped in the remaining columns
// with a hanging indent so continuation lines align under the value.
function labelBlock(label, value, labelWidth = 24) {
  const gutter = (label + " :").padEnd(labelWidth + 3, " ");
  const boldGutter = boldPrefix(gutter, label);
  const bodyWidth = W - gutter.length;
  const lines = wrap(value || "", bodyWidth);
  if (lines.length === 0) return [boldGutter];
  const out = [boldGutter + lines[0]];
  for (let i = 1; i < lines.length; i++) out.push(rep(" ", gutter.length) + lines[i]);
  return out;
}

const sigLine = () => rep("_", 32);
// Blank vertical space above a signature line so there is room to actually sign.
const SIG_SPACER = ["", ""];

const BANK_ADDRESS =
  "PJL Building, General Evangelista Street Corner Emilio Aguinaldo Highway, Zapote 1, Bacoor City, Cavite, Philippines";

export function buildDisclosureText(d) {
  const L = [];
  L.push(center("CEBUANA LHUILLIER RURAL BANK"));
  L.push(center("DISCLOSURE STATEMENT ON LOAN/CREDIT TRANSACTION"));
  L.push(center("(As Required under R.A. 3765, Truth in Lending Act)"));
  L.push(hr("="));
  L.push(...labelBlock("Name of the Borrower", d.name, 20));
  L.push(...labelBlock("Address", d.address, 20));
  L.push(...labelBlock("Loan Product", d.loanProduct, 20));
  L.push(...labelBlock("Loan Account Number", d.loanAccountNumber, 20));
  L.push(...labelBlock("Date Granted", d.dateGranted, 20));
  L.push(rightVal("LOAN GRANTED (Amount to be financed) PHP", money(d.loanAmount)));
  L.push(hr());
  L.push(b("FINANCE CHARGES"));
  L.push("  Interest " + d.contractualRatePct + "% add-on per month");
  L.push("    From " + d.fromDate + " To " + d.toDate);
  L.push(rightVal("      Not Deducted from Proceeds:", money(d.interestAmount)));
  L.push("  Other charges (Deducted from Proceeds):");
  L.push(rightVal("    c.1  Documentary Stamp", money(d.dst)));
  L.push(rightVal("TOTAL DEDUCTIONS FROM PROCEEDS OF LOAN", money(d.totalDeductions)));
  L.push(rightVal("NET PROCEEDS OF LOAN", money(d.netProceeds)));
  L.push(hr());
  L.push(b("EFFECTIVE INTEREST RATE:") + " " + d.eirPct + "%");
  L.push(
    ...wrap(
      "The effective interest rate is higher than the contractual interest rate of " +
        d.contractualRatePct +
        "% because of the deductions above."
    )
  );
  L.push(b("SCHEDULE OF PAYMENT") + "   (Payment Frequency: Monthly)");
  L.push(
    ...wrap(
      "  Total Installment Payments Payable in " +
        d.termMonths +
        " months at PHP " +
        money(d.monthlyAmort) +
        " each installment, starting on " +
        d.firstInstallmentDate +
        " and thereafter on every corresponding date of the succeeding agreed installment periods.",
      W
    )
  );
  L.push(b("COLLATERAL:") + "  [X] This loan is wholly/partly secured by: Jewelry");
  L.push(hr());
  L.push(b("ADDITIONAL/CONDITIONAL CHARGES IF CERTAIN STIPULATIONS ARE NOT MET:"));
  const c = d.charges || {};
  L.push(...labelBlock("Missed Payment Penalty", c.missed_payment_penalty, 24));
  L.push(
    ...labelBlock(
      "Prepayment Charges",
      c.prepayment_charges || "2% full interest amount scheduled for the month of prepayment.",
      24
    )
  );
  L.push(...labelBlock("Past Due Interest", c.past_due_interest, 24));
  L.push(...labelBlock("Past Due Penalty", c.past_due_penalty, 24));
  L.push(...labelBlock("Attorney's Fees", c.attorneys_fees, 24));
  L.push(hr());
  L.push(b("CERTIFIED CORRECT:"));
  L.push(...SIG_SPACER);
  L.push(sigLine());
  L.push("(Bank's Authorized Representative)");
  L.push(
    ...wrap(
      "I ACKNOWLEDGE RECEIPT OF A COPY OF THIS STATEMENT PRIOR TO THE CONSUMMATION OF THE CREDIT TRANSACTION AND THAT I UNDERSTAND AND FULLY AGREE TO THE TERMS AND CONDITIONS THEREOF."
    )
  );
  L.push(...SIG_SPACER);
  L.push(sigLine() + "        " + sigLine());
  L.push((d.name || "").slice(0, 32).padEnd(40, " "));
  L.push("(Signature over printed name/Date)      (Signature over printed name/Date)");
  L.push("BORROWER".padEnd(40, " ") + "BRANCH PAWNSHOP REP. (WITNESS)");
  return L.join("\n");
}

export function buildPnsaText(d) {
  const L = [];
  L.push(center("PROMISSORY NOTE AND SECURITY AGREEMENT"));
  L.push(center("No. " + (d.pnNumber || "________________")));
  L.push(hr("="));
  L.push(
    ...wrap(
      'For value received, the undersigned (the "Borrower/Grantor") jointly and severally promise to pay to the order of Cebuana Lhuillier Rural Bank, Inc. (the "Lender" or the "Bank"), at its office at ' +
        BANK_ADDRESS +
        ', or any of its branches or designated payment channels, the Loan Granted stated in Philippine currency (the "Loan"), and the corresponding Finance Charges from the date hereof until paid, according to the Loan Terms set as follows:'
    )
  );
  L.push(hr());
  L.push(rightVal("Date Granted", d.dateGranted || ""));
  L.push(rightVal("Loan Granted (Amount financed)", "PHP " + money(d.loanAmount)));
  L.push(
    rightVal(
      "Finance Charges (Interest " + d.contractualRatePct + "% add-on/month)",
      "PHP " + money(d.interestAmount)
    )
  );
  L.push(rightVal("Documentary Stamps (DST)", "PHP " + money(d.dst)));
  L.push(rightVal("Net Proceeds of Loan", "PHP " + money(d.netProceeds)));
  L.push(rightVal("Effective Interest Rate", d.eirPct + "%"));
  L.push(b("Schedule of Payment:"));
  L.push(
    ...wrap(
      "  " +
        d.termMonths +
        " monthly installments of PHP " +
        money(d.monthlyAmort) +
        " each, starting " +
        d.firstPaymentDate +
        "."
    )
  );
  L.push(hr());
  L.push(
    ...wrap(
      'The Borrower/Grantor acknowledges that the detailed terms, including the additional and/or conditional charges, repayment dates, installment amounts, and breakdown of principal and interest are fully set forth in the Disclosure Statement hereto attached and made an integral part hereof. The purpose/s of the Loan is/are for Personal Consumption. The Borrower/Grantor shall not use the Loan for any other purposes; otherwise, the Lender may terminate the Loan and immediately demand repayment of the outstanding balance thereof. In exchange for the granting of the Loan, the Borrower/Grantor shall surrender the physical possession of his/her Jewelry as specifically detailed in the corresponding Appraisal Card hereto attached as Annex "B" ("Collateral") in favor of the Lender. This act shall automatically create a first and preferred security interest over the said property to be covered under this Agreement. All obligations considered secured by this Agreement are hereinafter collectively referred to as the "Secured Obligations." The Borrower/Grantor acknowledges that this Promissory Note and Security Agreement is subject to the terms and conditions set forth in Annex "A" (Terms and Conditions) attached hereto and made an integral part hereof. The Borrower/Grantor confirms having read, understood, and agreed to all the provisions contained in Annex "A" and agrees to be bound thereby.'
    )
  );
  L.push(hr());
  L.push(b("CEBUANA LHUILLIER RURAL BANK, INC."));
  L.push(...SIG_SPACER);
  L.push(sigLine());
  L.push("By: (Signature over printed name) - Authorized Representative");
  L.push("");
  L.push(...SIG_SPACER);
  L.push(sigLine());
  L.push((d.name || "").slice(0, 40));
  L.push("BORROWER/GRANTOR (Signature over printed name)");
  L.push(...labelBlock("ADDRESS", d.address, 14));
  L.push(...labelBlock("E-MAIL ADDRESS", d.email, 14));
  return L.join("\n");
}

export function buildSimpleFormText(d) {
  const L = [];
  L.push(center("CEBUANA LHUILLIER BANK - JEWELFLEX FINANCING"));
  L.push(center("LOAN COMPUTATION"));
  L.push(hr("="));
  L.push("");
  L.push(b("BORROWER INFORMATION"));
  L.push(...labelBlock("Name", d.name, 22));
  L.push(...labelBlock("Address", d.address, 22));
  L.push("");
  L.push(b("LOAN DETAILS"));
  L.push(rightVal("Loan Term (months)", String(d.termMonths)));
  L.push(rightVal("Interest rate (add-on per month)", d.contractualRatePct + "%"));
  L.push(rightVal("Loan Amount (PHP)", money(d.loanAmount)));
  L.push(rightVal("Monthly Amortization (PHP)", money(d.monthlyAmort)));
  L.push(rightVal("DST on Promissory Note (PHP)", money(d.dst)));
  L.push(rightVal("Net Proceeds (PHP)", money(d.netProceeds)));
  L.push("");
  L.push(b("SCHEDULE"));
  L.push(rightVal("Date Granted", d.dateGranted));
  L.push(rightVal("First Payment Date", d.firstPaymentDate));
  L.push(hr());
  L.push(...SIG_SPACER);
  L.push(sigLine());
  L.push((d.name || "").slice(0, 40));
  L.push("Name of Borrower over Printed Name");
  L.push("");
  L.push(
    ...wrap(
      "This is a simulation for reference only. Final amounts are subject to verification and approval."
    )
  );
  return L.join("\n");
}

// Page 3 for the LX-310: fixed-width amortization schedule matching the
// "Amortization_JL Final" Excel. Columns: Due Date | Beginning Balance |
// Principal | Int Bal | Amort./Month.
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return "";
  const dd = String(x.getDate()).padStart(2, "0");
  return `${MON[x.getMonth()]} ${dd}, ${x.getFullYear()}`;
};

export function buildAmortizationText(d) {
  const { payment, monthlyRate, rows } = buildAmortizationSchedule({
    annualRate: d.annualRate,
    termMonths: d.termMonths,
    loanAmount: d.loanAmount,
    dateGranted: d.dateGranted,
  });
  const L = [];
  L.push(center("CEBUANA LHUILLIER RURAL BANK"));
  L.push(center("SCHEDULE OF AMORTIZATION"));
  L.push(hr("="));
  L.push(...labelBlock("Name", d.name, 14));
  L.push(...labelBlock("Address", d.address, 14));
  L.push(rightVal("Loan Amount (PHP)", money(d.loanAmount)));
  L.push(rightVal("Loan Term (months)", String(d.termMonths)));
  L.push(rightVal("Interest Rate (per annum)", money((Number(d.annualRate) || 0) * 100) + "%"));
  L.push(rightVal("Date Granted", shortDate(d.dateGranted)));
  L.push(hr());

  // Column widths (sum + 4 gaps <= 80): 13 + 15 + 13 + 12 + 13 = 66 + 4 = 70.
  const Wd = [13, 15, 13, 12, 13];
  const cols = (cells, bold = false) => {
    const s =
      String(cells[0]).padEnd(Wd[0]) +
      " " +
      String(cells[1]).padStart(Wd[1]) +
      " " +
      String(cells[2]).padStart(Wd[2]) +
      " " +
      String(cells[3]).padStart(Wd[3]) +
      " " +
      String(cells[4]).padStart(Wd[4]);
    return bold ? b(s) : s;
  };

  L.push(cols(["Due Date", "Beg. Balance", "Principal", "Int Bal", "Amort/Mo"], true));
  L.push(hr("-"));
  let totInt = 0;
  let totPrin = 0;
  let totAmt = 0;
  rows.forEach((r) => {
    totInt += r.intBal;
    totPrin += r.principal;
    totAmt += r.amort;
    L.push(
      cols([
        shortDate(r.dueDate),
        money(r.beginningBalance),
        money(r.principal),
        money(r.intBal),
        money(r.amort),
      ])
    );
  });
  L.push(hr("-"));
  L.push(cols([b("TOTAL"), "", money(totPrin), money(totInt), money(totAmt)]));
  L.push(hr());
  L.push(
    ...wrap(
      "Amortization computed on a diminishing-balance basis at " +
        (monthlyRate * 100).toFixed(4) +
        "% per month. Fixed monthly amortization: PHP " +
        money(payment) +
        "."
    )
  );
  L.push(b("Conforme:"));
  L.push(...SIG_SPACER);
  L.push(sigLine());
  L.push((d.name || "").slice(0, 40));
  L.push("(Signature over printed name)");
  return L.join("\n");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
// Escape for HTML, then turn bold markers into <b>...</b> for the popup preview.
function preHtml(s) {
  return escapeHtml(s)
    .split(BOLD_ON)
    .join("<b>")
    .split(BOLD_OFF)
    .join("</b>");
}
// ----------------------------------------------------------------------------
// Raw ESC/P output for the EPSON LX-310. Building a byte stream with the
// printer's own control codes lets the printer render its internal font
// directly — the crispest result (no browser/driver rasterisation). The user
// sends the file raw to the printer (a queue configured with the RAW
// datatype, NOT the normal GDI queue), e.g.:
//   Windows : copy /b Disclosure.prn "\\localhost\EPSON LX-310 RAW"
//   macOS   : lp -o raw -d EPSON_LX_310 Disclosure.prn
//   Linux   : cat Disclosure.prn > /dev/usb/lp0   (or lp -o raw)
// ----------------------------------------------------------------------------

// Map/strip anything that isn't safe single-byte ASCII. A stray 0x1B (ESC) or
// other control byte inside the text would corrupt the whole ESC/P stream, and
// the LX-310 is not UTF-8 aware — so non-ASCII must become ASCII equivalents.
const ESCP_MAP = {
  "\u20B1": "PHP", // ₱ peso sign
  "\u00D1": "N", // Ñ
  "\u00F1": "n", // ñ
  "\u2013": "-", // – en dash
  "\u2014": "-", // — em dash
  "\u2018": "'", // ‘
  "\u2019": "'", // ’
  "\u201C": '"', // “
  "\u201D": '"', // ”
  "\u2026": "...", // …
  "\u00A0": " ", // non-breaking space
};
function sanitizeAscii(s) {
  let out = "";
  for (const ch of String(s)) {
    if (ESCP_MAP[ch] !== undefined) {
      out += ESCP_MAP[ch];
      continue;
    }
    const c = ch.charCodeAt(0);
    if (c === 0x09) out += "    "; // tab → spaces
    else if (c >= 0x20 && c <= 0x7e) out += ch; // printable ASCII
    else out += "?"; // control / non-ASCII → placeholder (never emit raw ESC)
  }
  return out;
}

export function buildEscpDocument(pages, { lpi = 6, pageInches = 11, quality = "lq" } = {}) {
  const bytes = [];
  const push = (...b) => {
    for (const x of b) bytes.push(x & 0xff);
  };
  const text = (s) => {
    // Emit bytes for one line, translating bold markers into ESC/P emphasized
    // ON/OFF control codes. Everything else is sanitized to safe ASCII.
    for (const ch of String(s)) {
      if (ch === BOLD_ON) {
        push(0x1b, 0x45); // ESC E — emphasized (bold) ON
        continue;
      }
      if (ch === BOLD_OFF) {
        push(0x1b, 0x46); // ESC F — emphasized (bold) OFF
        continue;
      }
      const clean = sanitizeAscii(ch);
      for (let i = 0; i < clean.length; i++) push(clean.charCodeAt(i));
    }
  };
  const list = (Array.isArray(pages) ? pages : [pages]).filter((p) => p != null);

  push(0x1b, 0x40); // ESC @   — initialise / reset printer
  push(0x1b, 0x78, quality === "draft" ? 0x00 : 0x01); // ESC x n — 1=LQ, 0=draft
  push(0x1b, 0x6c, 0x00); // ESC l 0 — left margin = column 0
  push(0x1b, 0x50); // ESC P   — 10 cpi (Pica) pitch → 80 cols / 8"
  push(0x1b, lpi === 8 ? 0x30 : 0x32); // ESC 0 = 8 lpi, ESC 2 = 6 lpi
  push(0x1b, 0x43, 0x00, pageInches); // ESC C NUL n — page length = n inches

  list.forEach((pg) => {
    for (const line of String(pg).split("\n")) {
      text(line);
      push(0x1b, 0x46); // ESC F — ensure bold is OFF at end of every line
      push(0x0d, 0x0a); // CR LF
    }
    push(0x0c); // FF — advance to top of next page / tear-off
  });

  push(0x1b, 0x40); // ESC @ — reset when done
  return new Uint8Array(bytes);
}

export function downloadEscp(pages, { filename = "print.prn", lpi = 6, quality = "lq" } = {}) {
  const data = buildEscpDocument(pages, { lpi, quality });
  const blob = new Blob([data], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".prn") ? filename : filename + ".prn";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return true;
}



// Opens a clean text-only window and triggers the print dialog. `pages` is an
// array of page strings (e.g. [disclosure, pnsa]); each renders in its own
// <pre> with a real CSS page break so nothing spills across sheets. The font
// size is auto-fitted so the tallest page always fits on one page.
export function openTextPrint(pages, { title = "Print", paper = "letter" } = {}) {
  const list = (Array.isArray(pages) ? pages : [pages]).filter((p) => p != null);
  // wPt/hPt = printable width/height in points (paper minus margins). Margins
  // are kept generous horizontally so the printer's own hardware margin never
  // clips the edge characters.
  const PAPER = {
    letter: { size: "8.5in 11in", margin: "0.45in 0.45in", wPt: (8.5 - 0.9) * 72, hPt: (11 - 0.9) * 72 },
    a4: { size: "210mm 297mm", margin: "12mm 12mm", wPt: ((210 - 24) / 25.4) * 72, hPt: ((297 - 24) / 25.4) * 72 },
    cont8: { size: "8in 11in", margin: "0.45in 0.4in", wPt: (8 - 0.8) * 72, hPt: (11 - 0.9) * 72 },
  };
  const cfg = PAPER[paper] || PAPER.letter;
  const COLS = 80;
  const CHAR_W = 0.62; // Courier advance width (em) incl. safety margin
  const MAX_FONT = 12;
  const MIN_FONT = 8.5;

  // Pick the largest font (<=12pt) at which `lines` fit the page by BOTH width
  // (80 columns) and height, then choose a line-height that fills the sheet
  // without overlapping (1.05) or over-spacing (1.4).
  const fitPage = (lines) => {
    const byWidth = cfg.wPt / (COLS * CHAR_W);
    const byHeight = cfg.hPt / (lines * 1.05);
    let font = Math.min(MAX_FONT, byWidth, byHeight);
    font = Math.max(MIN_FONT, font);
    font = Math.floor(font * 10) / 10;
    let lh = cfg.hPt / (lines * font);
    lh = Math.max(1.05, Math.min(1.4, lh));
    lh = Math.round(lh * 1000) / 1000;
    return { font, lh };
  };

  const w = window.open("", "_blank", "width=880,height=1000");
  if (!w) {
    // eslint-disable-next-line no-alert
    alert("Please allow pop-ups for this site to use Text Print (LX-310).");
    return false;
  }
  // Each page is sized INDEPENDENTLY so it reads as large as possible while
  // still fitting on one sheet.
  const body = list
    .map((t, i) => {
      const lines = String(t).split("\n").length;
      const { font, lh } = fitPage(lines);
      const last = i === list.length - 1 ? " last" : "";
      return `<pre class="page${last}" style="font-size:${font}pt;line-height:${lh}">${preHtml(t)}</pre>`;
    })
    .join("");
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: ${cfg.size}; margin: ${cfg.margin}; }
  html, body { margin: 0; padding: 0; background: #fff; }
  pre {
    font-family: 'Courier New', Courier, monospace;
    color: #000;
    white-space: pre;
    margin: 0;
    -webkit-font-smoothing: none;
  }
  .page { page-break-after: always; break-after: page; }
  .page.last { page-break-after: auto; break-after: auto; }
  @media screen { body { padding: 20px; } .page { border-bottom: 1px dashed #ccc; padding-bottom: 16px; margin-bottom: 16px; } .page.last { border-bottom: none; } }
  @media print { .screen-hint { display: none; } }
  .screen-hint { font-family: sans-serif; font-size: 12px; color: #555; margin-bottom: 12px; }
</style></head>
<body>
  <div class="screen-hint">Tip: In the print dialog set <b>Scale = 100%</b> (not "Fit to page") and turn OFF "Headers and footers" for a clean dot-matrix printout.</div>
  ${body}
</body></html>`
  );
  w.document.close();
  w.focus();
  setTimeout(() => {
    try {
      w.print();
    } catch (e) {
      /* user can print manually */
    }
  }, 350);
  return true;
}
